import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/utils/cn';
import { useClientsAndInvitations } from '../../hooks/useClientsAndInvitations';
import { useShareFpQaEngagement } from '../../hooks/useFpQaEngagements';
import type { ShareFpQaEngagementPayload } from '../../types/fp-qa.types';
import { ShareCarePlanChoiceStep } from './ShareCarePlanChoiceStep';
import { ShareCarePlanPickerStep } from './ShareCarePlanPickerStep';
import {
  extractShareError,
  type DeliveryMode,
  type PickerTab,
} from './share-care-plan-modal.types';

type ShareCarePlanModalProps = {
  open: boolean;
  engagementId: string;
  planLabel: string;
  /** Pre-selected client carried over from the engagement, if any. */
  preselectClientUserId: string | null;
  /**
   * Triggers an authenticated PDF download for the engagement. Passed
   * from ResultsPage so we reuse the same JWT-aware blob helper that
   * powers the top-level "Download care plan" button (`window.open`
   * would skip the apiClient Authorization header and 401).
   */
  onPreviewPdf: () => void | Promise<void>;
  /** True while the authenticated PDF blob fetch is in flight. */
  isPdfDownloading: boolean;
  onClose: () => void;
};

/**
 * Two-step modal that lets an FP send the care plan to a client. Step 1
 * picks the delivery (email vs in-app), Step 2 picks the recipient
 * (existing-client search OR invite-a-new-client form).
 *
 * Send calls the real share endpoint via `useShareFpQaEngagement`: the picker
 * step reflects live sending/success/error states, and a successful share
 * auto-dismisses the modal.
 */
export const ShareCarePlanModal = ({
  open,
  engagementId,
  planLabel,
  preselectClientUserId,
  onPreviewPdf,
  isPdfDownloading,
  onClose,
}: ShareCarePlanModalProps) => {
  const [mode, setMode] = useState<DeliveryMode | null>(null);
  const [pickerTab, setPickerTab] = useState<PickerTab>('existing');
  const [search, setSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(
    preselectClientUserId,
  );
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientName, setNewClientName] = useState('');

  const { data: entries = [], isLoading } = useClientsAndInvitations();
  const shareMutation = useShareFpQaEngagement(engagementId);

  // In-app delivery requires an existing account, so it is unavailable on the
  // "invite a new client" (off-platform) tab — direct the FP to email instead.
  const inAppNewBlocked = mode === 'in-app' && pickerTab === 'new';

  // One-care-seeker-per-plan rule: when the plan is attributed to a client,
  // in-app sharing is locked to that client (the backend rejects any other
  // in-app recipient). Email stays free for sending the PDF elsewhere.
  const inAppLocked = mode === 'in-app' && Boolean(preselectClientUserId);

  // Force the existing-clients tab + the attributed client whenever in-app is
  // chosen for an attributed plan, so the FP can't aim a doomed in-app share.
  useEffect(() => {
    if (mode === 'in-app' && preselectClientUserId) {
      setPickerTab('existing');
      setSelectedClientId(preselectClientUserId);
    }
  }, [mode, preselectClientUserId]);

  const buildPayload = (): ShareFpQaEngagementPayload | null => {
    if (!mode) return null;
    if (pickerTab === 'existing') {
      return selectedClientId ? { deliveryMode: mode, clientUserId: selectedClientId } : null;
    }
    const email = newClientEmail.trim();
    if (!email) return null;
    return {
      deliveryMode: mode,
      recipientName: newClientName.trim() || undefined,
      recipientEmail: email,
    };
  };

  const handleSend = () => {
    const payload = buildPayload();
    if (!payload || inAppNewBlocked) return;
    shareMutation.mutate(payload);
  };

  const connectedClients = useMemo(() => {
    return entries
      .filter((e) => e.type === 'CONNECTED')
      .map((e) => {
        if (e.type !== 'CONNECTED') return null;
        const name =
          [e.firstName, e.lastName].filter(Boolean).join(' ').trim() || e.email;
        return { id: e.id, name, email: e.email };
      })
      .filter((c): c is { id: string; name: string; email: string } => Boolean(c));
  }, [entries]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return connectedClients;
    return connectedClients.filter(
      (c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
    );
  }, [connectedClients, search]);

  // Resolve the attributed client for the in-app lock notice. If the client
  // isn't in the connected list (e.g. relationship removed) fall back to the
  // normal picker and let the backend enforce the rule.
  const lockedClient = inAppLocked
    ? (connectedClients.find((c) => c.id === preselectClientUserId) ?? null)
    : null;

  // Reset all transient modal state. Shared by the explicit close handler and
  // the programmatic-close effect below.
  const resetState = () => {
    setMode(null);
    setPickerTab('existing');
    setSearch('');
    setNewClientEmail('');
    setNewClientName('');
    setSelectedClientId(preselectClientUserId);
    shareMutation.reset();
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // The controlled <Dialog> only fires onOpenChange on user interaction, so a
  // programmatic close (parent flips `open` to false on a route change or state
  // reset) would otherwise leave mode/tab/inputs/mutation stale for the next
  // open. Reset on that transition.
  useEffect(() => {
    if (!open) resetState();
    // resetState only calls stable setters + mutation.reset; key off `open` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Drive the step transition direction: going from the choice step (depth 0)
  // into the picker (depth 1) slides in from the right; pressing Back slides
  // in from the left. We read the previous depth during render and commit the
  // new one after paint so the keyed container animates the correct way.
  const stepDepth = mode === null ? 0 : 1;
  const prevStepDepthRef = useRef(stepDepth);
  const isForward = stepDepth >= prevStepDepthRef.current;
  useEffect(() => {
    prevStepDepthRef.current = stepDepth;
  }, [stepDepth]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            Share &ldquo;{planLabel}&rdquo;
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            Send this care guide to your client. Choose how it gets delivered, then
            pick the recipient.
          </DialogDescription>
        </DialogHeader>

        <div
          key={mode === null ? 'choice' : 'picker'}
          className={cn(
            'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200 motion-safe:fill-mode-both',
            isForward
              ? 'motion-safe:slide-in-from-right-2'
              : 'motion-safe:slide-in-from-left-2',
          )}
        >
          {mode === null ? (
            <ShareCarePlanChoiceStep
              onChoose={setMode}
              onPreviewPdf={() => void onPreviewPdf()}
              isPdfDownloading={isPdfDownloading}
            />
          ) : (
            <ShareCarePlanPickerStep
              mode={mode}
              pickerTab={pickerTab}
              onTabChange={setPickerTab}
              search={search}
              onSearchChange={setSearch}
              isLoadingClients={isLoading}
              clients={filteredClients}
              selectedClientId={selectedClientId}
              onSelectClient={setSelectedClientId}
              newClientName={newClientName}
              onNewClientNameChange={setNewClientName}
              newClientEmail={newClientEmail}
              onNewClientEmailChange={setNewClientEmail}
              inAppNewBlocked={inAppNewBlocked}
              lockedClient={lockedClient}
              onSend={handleSend}
              isSending={shareMutation.isPending}
              isSuccess={shareMutation.isSuccess}
              errorMessage={extractShareError(shareMutation.error)}
              onBack={() => setMode(null)}
              onClose={handleClose}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
